import { useState } from 'react';

export interface PickerOption {
  id: string;
  label: string;
  /** Optional group label (e.g. "My library" vs "Shared library") — options render grouped when
   * any option in the list specifies one. */
  group?: string;
}

/**
 * Replaces a plain `<select>` with one `<option>` per candidate — unusable once there are
 * hundreds of candidates — with a text input that filters as you type, plus a list of matching,
 * individually-clickable (and therefore Tab-reachable) buttons. No new dependency: this project
 * has never taken a UI component library, and a few hundred plain-text candidates don't need one.
 *
 * Defaults to single-select: clicking a candidate immediately commits via `onSelect` and closes.
 * Pass `multi` (with `onSelectMany`) to stage several candidates instead — clicking toggles a
 * candidate in/out of the staged set without closing, and a commit button adds all of them at
 * once. Single-select callers (e.g. the "add to playlist" picker on a video's detail page) are
 * unaffected by this mode existing.
 */
export function SearchablePicker({
  options,
  onSelect,
  onSelectMany,
  multi,
  placeholder,
  disabled,
}: {
  options: PickerOption[];
  onSelect?: (id: string) => void;
  /** Required when `multi` is true — commits every staged id at once. */
  onSelectMany?: (ids: string[]) => void;
  multi?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [stagedIds, setStagedIds] = useState<Set<string>>(new Set());

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  const groups = new Map<string, PickerOption[]>();
  for (const opt of filtered) {
    const key = opt.group ?? '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(opt);
  }

  const toggleStaged = (id: string) =>
    setStagedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const commitStaged = () => {
    onSelectMany?.([...stagedIds]);
    setStagedIds(new Set());
    setQuery('');
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Delay closing so a click on an option (which blurs the input first) still registers.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder ?? 'Search…'}
        disabled={disabled}
        style={{ width: '100%' }}
      />
      {open && options.length > 0 && (
        <div
          className="card"
          style={{
            position: 'absolute',
            zIndex: 10,
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 260,
            overflowY: 'auto',
            padding: 'var(--space-2)',
          }}
        >
          {multi && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={commitStaged}
              disabled={disabled || stagedIds.size === 0}
              style={{ width: '100%', marginBottom: 'var(--space-2)' }}
            >
              Add {stagedIds.size} selected
            </button>
          )}
          {filtered.length === 0 && <p className="muted">No matches.</p>}
          {[...groups.entries()].map(([groupName, opts]) => (
            <div key={groupName || '_'}>
              {groupName && (
                <div className="muted" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                  {groupName}
                </div>
              )}
              {opts.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className="secondary"
                  style={{ width: '100%', textAlign: 'left', display: 'flex', gap: 8, marginBottom: 2 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (multi) {
                      toggleStaged(opt.id);
                    } else {
                      onSelect?.(opt.id);
                      setQuery('');
                      setOpen(false);
                    }
                  }}
                >
                  {multi && (
                    <input
                      type="checkbox"
                      checked={stagedIds.has(opt.id)}
                      readOnly
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  {opt.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
